import { useState, useMemo } from 'react'
import { MultiSelectDiffObject, useWorkspace } from '@opallabs/hooks'
import { useMomentData, useBoardData } from './queries'
import { BoardData, BoardOption, MomentData, MomentToBoardState } from './types'
import { useErrorState } from './useErrorState'

type UseAddBoardStateArgs = {
  momentUuids: string[]
  unscheduleByDefault: boolean
}

const findSharedBoards = (moments: MomentData[]) => {
  if (moments.length === 1) return Object.keys(moments[0].boardObjectsByBoard)

  // When multiple moments are being updated, only show
  // inital selection as boards every moment shares
  const sharedBoardCounts = moments.reduce(
    (accum, current) => {
      const boardIds = Object.keys(current.boardObjectsByBoard)
      boardIds.forEach((id) => {
        if (accum[id]) {
          accum[id] += 1
        } else {
          accum[id] = 1
        }
      })
      return accum
    },
    {} as Record<string, number>
  )
  return Object.entries(sharedBoardCounts)
    .filter(([, count]) => count === moments.length)
    .map(([id]) => id)
}

const mapBoardOptions = (board: BoardData): BoardOption => ({
  value: board.id,
  label: board.title,
  meta: {
    defaultStoryId: board.defaultStoryId,
  },
})

export const useAddBoardState = ({
  momentUuids,
  unscheduleByDefault,
}: UseAddBoardStateArgs): MomentToBoardState => {
  const { uuid: workspaceId } = useWorkspace()
  const [selectedBoards, setSelectedBoards] =
    useState<MultiSelectDiffObject<BoardOption> | null>(null)
  const [unschedule, setUnschedule] = useState(unscheduleByDefault)

  const boardsFetchResult = useBoardData(workspaceId)
  const momentFetchResult = useMomentData({ momentUuids })

  const canUnschedule = useMemo(
    () =>
      Boolean(
        momentFetchResult.data?.length === 1 &&
          momentFetchResult.data[0].scheduleId
      ),
    [momentFetchResult.data]
  )

  const initialSelection = useMemo<BoardOption[]>(() => {
    if (
      !momentFetchResult.fetchingComplete ||
      !boardsFetchResult.data ||
      !momentFetchResult.data
    )
      return []

    const boardUuids = findSharedBoards(momentFetchResult.data)
    const selectedBoards = boardsFetchResult.data.filter((board) =>
      boardUuids.includes(board.id)
    )

    return selectedBoards.map(mapBoardOptions)
  }, [boardsFetchResult.data, momentFetchResult])

  const boardOptions = useMemo<BoardOption[]>(() => {
    return (boardsFetchResult.data ?? []).map(mapBoardOptions)
  }, [boardsFetchResult.data])

  const { hasError, isLoading, showEmptyBoardErrorAfterInteraction } =
    useErrorState({
      allowUnscheduling: momentUuids.length === 1,
      boardsFetchResult,
      momentFetchResult,
      initialSelection,
      selectedBoards,
      unschedule,
    })

  const boardSelectionChanged = () =>
    Boolean(selectedBoards?.added.length || selectedBoards?.removed.length)

  return {
    boardOptions,
    boardSelectionChanged,
    canUnschedule,
    hasError,
    initialSelection,
    isLoading,
    moments: momentFetchResult.data ?? [],
    selectedBoards,
    setSelectedBoards,
    setUnschedule,
    showEmptyBoardErrorAfterInteraction,
    unschedule,
  }
}
